import { useAppStore, useWidgetStore } from '@features/common/store/AppStore';
import { WidgetProps } from '@features/dashboard/types';
import { Close as CloseTabIcon, Add as NewTabIcon } from '@mui/icons-material';
import { Divider, Tab, Tabs } from '@mui/material';
import { Order } from '@protos/v2/order';
import { ColDef, ValueFormatterFunc } from 'ag-grid-enterprise';
import { format } from 'date-fns';
import Widget, { Header } from '../Widget';
import Settings from '../settings/Settings';
import { AmountCellRenderer, OrderTrackerActionCellRenderer } from './OrderTrackerComponents';
import { OrderTrackerTab } from './OrderTrackerTab';
import { getDefaultTabName, OrderTrackerStore } from './store/OrderTrackerStore';
import { OrderTrackerTabData } from './store/OrderTrackerTypes';
import { useDashboardContext } from '@shared/contexts/DashboardContext';

import { useEffect } from 'react';
import { useTradingPermissions } from '../trading-v2/hooks/useTradingPermissions';

export type OrderTrackerWidgetPayload = {
  tabs: OrderTrackerTabData[];
  tabIndex: number;
};

const orderIdFormatter: ValueFormatterFunc<Order, string> = params => {
  return params.value || '';
};

const textFormatter: ValueFormatterFunc<Order, string> = params => {
  return (params.value || '').replaceAll('_', ' ').toUpperCase();
};

const dateFormatter: ValueFormatterFunc<Order, string> = params => {
  if (!params.value) return '';
  return format(new Date(params.value), 'dd/MM/yyyy HH:mm:ss.SSS');
};

const symbolFormatter: ValueFormatterFunc<Order, string | { front: string; back: string }> = ({ value }) =>
  value ? (typeof value === 'string' ? value.toUpperCase() : `${value.front} / ${value.back}`.toUpperCase()) : '';

const OrderTrackerColumnDefinitions: ColDef<Order>[] = [
  { field: 'id', valueFormatter: orderIdFormatter, headerName: 'ID' },
  { field: 'created_timestamp', valueFormatter: dateFormatter, sort: 'desc', headerName: 'CREATED', cellDataType: 'dateString' },
  { field: 'exchange_last_updated_timestamp', valueFormatter: dateFormatter, sort: 'desc', headerName: 'UPDATED', cellDataType: 'dateString' },
  { field: 'symbol', valueFormatter: symbolFormatter },
  { field: 'exchange', valueFormatter: textFormatter, maxWidth: 100 },
  { field: 'executed_amount', cellRenderer: AmountCellRenderer, cellStyle: { padding: 0 }, headerName: 'SIDE / AMOUNT', maxWidth: 140 },
  { field: 'price' },
  { field: 'order_type', valueFormatter: textFormatter },
  { field: 'order_state', valueFormatter: textFormatter },
  { field: 'user.full_name', valueFormatter: textFormatter },
  { field: undefined, cellRenderer: OrderTrackerActionCellRenderer, headerName: 'ACTIONS', cellStyle: { padding: 0 }, maxWidth: 100 },
];

export type OrderTrackerWidgetProps = WidgetProps & { payload?: OrderTrackerWidgetPayload };
type OrderTrackerWidgetContentProps = OrderTrackerWidgetProps & { tradingAccounts: string[] };

const OrderTrackerWidgetContent = (props: OrderTrackerWidgetContentProps) => {
  const { apiClient, user } = useAppStore();

  const { id, ...otherProps } = props;
  const store = useWidgetStore<OrderTrackerStore>(id);
  const { tabs, tabIndex, addTab, deleteTab, setTabIndex, updateTab, getPayloadFromState, setTradingAccounts } = store;

  const { editWidgetPayloadById } = useDashboardContext();

  useEffect(() => {
    // TODO- rehydrating widgets should happen elsewhere, this is just to keep consistency with
    // the rest of the current flux system
    if (props.payload) store.initFromPayload(props.payload);

    // TODO: the store should get populated with these upstream of the widget
    setTradingAccounts(props.tradingAccounts);
  }, [props.tradingAccounts]);

  const updatePayload = () => {
    if (getPayloadFromState) {
      editWidgetPayloadById(id, getPayloadFromState());
    }
  };

  const onChangeTab = (event: React.SyntheticEvent, newValue: string | number) => {
    const newIndex = Number(newValue);
    if (newIndex === tabs.length) {
      addTab();
      updatePayload();
      return;
    } else {
      setTabIndex(newIndex);
      updatePayload();
    }
  };

  const handleTabUpdate = (tabIndex: number, data: Partial<OrderTrackerTabData>) => {
    updateTab(tabIndex, data);
    updatePayload();
  };

  const handleTabDeleted = (index: number) => {
    deleteTab(index);
    updatePayload();
  };

  let content: JSX.Element = <></>;

  if (apiClient && user) {
    content = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          padding: 0,
          width: '100%',
          height: '100%',
          minWidth: '1000px',
          overflowX: 'scroll',
        }}
      >
        <Tabs value={tabIndex} onChange={onChangeTab} sx={{ paddingTop: 0, minHeight: '42px', height: '42px' }}>
          {tabs.map((tp, index) => {
            const icon = (
              <CloseTabIcon
                sx={{ transition: 'all 0.1s ease', opacity: 0.5, ':hover': { color: 'white', opacity: 1 } }}
                fontSize="small"
                onClick={e => {
                  e.stopPropagation();
                  handleTabDeleted(index);
                }}
              />
            );

            let tabName = tp.tabName || getDefaultTabName(tp);
            if (tabName?.length > 45) {
              tabName = tabName.substring(0, 42) + '...';
            }
            return (
              <Tab
                id={tp.index.toString()}
                label={tabName}
                icon={icon}
                iconPosition={'end'}
                key={tp.index.toString()}
                sx={{ minHeight: '26px', height: '26px', paddingTop: '20px', fontSize: '.9em' }}
              />
            );
          })}
          <Tab
            sx={{ ':hover': { color: 'white' }, textOverflow: 'ellipsis' }}
            icon={<NewTabIcon sx={{ transition: 'all 0.1s ease' }} key={'addTab'} />}
          />
        </Tabs>

        <Divider />

        {tabs.map((tp, index) => {
          const hidden = index !== tabIndex;
          const key = 'tab' + index;

          if (hidden) {
            return <div key={key}></div>;
          }

          return (
            <div role="tabpanel" hidden={hidden} key={key} id={tp.index.toString()} style={{ width: '100%', height: '100%', flexGrow: 1 }}>
              <OrderTrackerTab id={id} columns={OrderTrackerColumnDefinitions} index={index} handleTabUpdate={handleTabUpdate} />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Widget {...otherProps} id={id}>
      <Header title={otherProps.title}>
        <Settings id={`order-tracker-${id}`} widgetId={id} />
      </Header>
      {content}
    </Widget>
  );
};

export const OrderTrackerWidget = (props: OrderTrackerWidgetProps) => {
  const { user, getWidgetData } = useAppStore();

  // TODO: move this into the store
  const { tradingAccounts } = useTradingPermissions(user ? user : undefined);

  if (getWidgetData(props.id)) {
    return <OrderTrackerWidgetContent {...props} tradingAccounts={tradingAccounts} />;
  } else {
    return <></>;
  }
};
